import vouchersService from '../../../services/voucher/vouchers-service';
import teamMembersService from '../../../services/team-members-service';
import userEventService from '../../../services/user-event-service';
import { clearLeaseAbstractionValidationForDeal } from '../../../utils/lease-abstraction-validation-storage';
import {
  FETCH_VOUCHER_SUCCESS,
  FETCH_RECIPIENTS_SUCCESS,
  FETCH_OFFICE_TYPES,
  FETCH_COMMERCIAL_PROPERTIES,
  FETCH_MTA_NAICS_CHOICES,
  FETCH_MTA_NAICS_CHOICES_BY_CODE,
  UPDATE_COMPANY,
  FETCH_COMPANY_BY_ID,
  UPDATE_COMMERCIAL_PROPERTIES,
  DELETE_COMMERCIAL_PROPERTIES,
  CREATE_COMMERCIAL_PROPERTIES,
} from '../action-types';

// Action creators
const fetchVoucherSuccess = voucher => ({
  type: FETCH_VOUCHER_SUCCESS,
  payload: voucher,
});

const fetchRecipientsSuccess = recipients => ({
  type: FETCH_RECIPIENTS_SUCCESS,
  payload: recipients,
});

const fetchOfficeTypesSuccess = officeTypes => ({
  type: FETCH_OFFICE_TYPES,
  payload: officeTypes,
});

const fetchUpdatedCompanyInfoSuccess = updatedInfo => ({
  type: UPDATE_COMPANY,
  payload: updatedInfo,
});

const fetchMtaNaicsChoicesByCodesSuccess = searchValues => ({
  type: FETCH_MTA_NAICS_CHOICES_BY_CODE,
  payload: searchValues,
});

const fetchCommercialPropertySuccess = commercialProperties => ({
  type: FETCH_COMMERCIAL_PROPERTIES,
  payload: commercialProperties,
});

const fetchMtaNaicsChoicesSuccess = mtaNaicsChoices => ({
  type: FETCH_MTA_NAICS_CHOICES,
  payload: mtaNaicsChoices,
});

const fetchCompanyByIdSuccess = companyInfo => ({
  type: FETCH_COMPANY_BY_ID,
  payload: companyInfo,
});

const createCommercialPropertySuccess = commercialProperty => ({
  type: CREATE_COMMERCIAL_PROPERTIES,
  payload: commercialProperty,
});

const deleteCommercialPropertySuccess = commercialProperty => ({
  type: DELETE_COMMERCIAL_PROPERTIES,
  payload: commercialProperty,
});

const updateCommercialPropertySuccess = commercialProperty => ({
  type: UPDATE_COMMERCIAL_PROPERTIES,
  payload: commercialProperty,
});

// Async actions creators
const fetchVoucher = voucherId => (dispatch) => {
  vouchersService.fetchVoucher(voucherId)
    .then(voucher => dispatch(fetchVoucherSuccess(voucher)))
    .catch(error => console.log('Error fetching voucher'));
};

const fetchVoucherByDealId = dealId => (dispatch) => {
  vouchersService.fetchVoucherByDealId(dealId)
    .then(voucher => dispatch(fetchVoucherSuccess(voucher)))
    .catch(error => console.log('Error fetching voucher'));
};

const deleteCommercialProperty = id => (dispatch) => {
  vouchersService.deleteCommercialProperty(id)
    .then(commercialproperty => dispatch(deleteCommercialPropertySuccess(commercialproperty)))
    .catch(error => console.log('Error deleting commercial property'));
};

const fetchCommercialProperty = dealId => (dispatch) => {
  return vouchersService.fetchCommercialProperty(dealId)
    .then(commercialproperty => dispatch(fetchCommercialPropertySuccess(commercialproperty.results)))
    .catch(error => console.log('Error fetching commercial property'));
};

// A new deal has no property until the Lease Information accordion is first opened (that is
// what creates it), so the Review page creates it too -- otherwise the address fields are never
// asked for there. Creates only after a successful fetch that returned no property.
const pendingPropertyCreates = {};
const ensureCommercialProperty = dealId => dispatch => dispatch(fetchCommercialProperty(dealId)).then((action) => {
  if (!Array.isArray(action?.payload) || action.payload.length > 0) return action;
  if (!pendingPropertyCreates[dealId]) {
    pendingPropertyCreates[dealId] = vouchersService.createCommercialProperty({ country: 'United States', deal: dealId })
      .finally(() => { delete pendingPropertyCreates[dealId]; });
  }
  return pendingPropertyCreates[dealId]
    .then(() => dispatch(fetchCommercialProperty(dealId)))
    .catch(() => action);
});

const createCommercialProperty = params => (dispatch) => {
  vouchersService.createCommercialProperty(params)
    .then(commercialproperty => dispatch(createCommercialPropertySuccess(commercialproperty)))
    .catch(error => console.log('Error creating commercial property'));
};

const fetchMtaNaicsChoices = () => (dispatch) => {
  vouchersService.getMtaNaicsChoices()
    .then(mtaNaicsChoices => dispatch(fetchMtaNaicsChoicesSuccess(mtaNaicsChoices)))
    .catch(error => console.log('Error fetching Naics choices'));
};

const fetchMtaNaicsChoicesByCode = searchText => (dispatch) => {
  vouchersService.searchMtaNaicsChoicesByCode(searchText)
    .then(mtaNaicsChoices => dispatch(fetchMtaNaicsChoicesByCodesSuccess(mtaNaicsChoices)))
    .catch(error => console.log('Error fetching Naics Choices by code'));
};

const fetchOfficeTypes = () => (dispatch) => {
  vouchersService
    .fetchVoucherOfficeTypes()
    .then(officeTypes => dispatch(fetchOfficeTypesSuccess(officeTypes)))
    .catch(error => console.log('Error fetching office types'));
};

const fetchCompanyById = id => (dispatch) => {
  vouchersService
    .fetchCompanyById(id)
    .then(companyInfo => dispatch(fetchCompanyByIdSuccess(companyInfo)))
    .catch(error => console.log('Error fetching company'));
};

const createVoucher = dealId => (dispatch) => {
  vouchersService.createVoucher(dealId)
    .then(voucher => dispatch(fetchVoucherSuccess(voucher)))
    .catch(error => console.log('Error creating voucher'));
};

const updateCompany = (id, params) => dispatch => vouchersService.updateCompany(id, params)
  .then((company) => {
    dispatch(fetchUpdatedCompanyInfoSuccess(company));
    return company;
  })
  .catch(() => {
    console.log('Error updating company');
    return undefined;
  });

// Syncs a company record already fetched/created elsewhere (e.g. Review's Company Name
// search selecting/creating a company) into the same state.voucher.updatedInfo slot
// `updateCompany` above populates -- lets other components pick up the fresh data without
// an extra GET refetch of a record they were just given.
const setUpdatedCompanyInfo = company => (dispatch) => {
  dispatch(fetchUpdatedCompanyInfoSuccess(company));
};

const updateCommercialProperty = (id, params) => dispatch => vouchersService.updateCommercialProperty(id, params)
  .then((commercialProperty) => {
    dispatch(updateCommercialPropertySuccess(commercialProperty));
    return commercialProperty;
  })
  .catch(() => {
    console.log('Error updating commercial property');
    return undefined;
  });

const fetchOrCreateVoucher = dealId => (dispatch) => {
  vouchersService.fetchOrCreateVoucher(dealId)
    .then(voucher => dispatch(fetchVoucherSuccess(voucher)))
    .catch(error => console.log('Error fetching or creating voucher'));
};

const updateVoucher = (id, params) => dispatch => vouchersService.updateVoucher(id, params)
  .then((voucher) => {
    dispatch(fetchVoucherSuccess(voucher));
    return voucher;
  })
  .catch(error => console.log('Error updating voucher'));

// Mirrors the Angular voucher flow's send.js: intentionally fired for every submit ATTEMPT,
// not just successful ones, so EventHorizon has a record even when the submit request itself
// fails. submitVoucher performs no pre-submit validation of its own (the UI only enables the
// submit action once voucher validation passes), so there's no gap here where this could fire
// without a submit request actually being made right after it.
const trackVoucherSubmitted = (getState) => {
  const { deal: dealId, send_to_email_addresses: recipientList } = getState().voucher.voucher;

  teamMembersService.getDealTeam({ deal: dealId })
    .then((data) => {
      userEventService.trackEvent({
        eventCategory: 'Voucher Submission',
        eventAction: 'voucher_has_been_submitted',
        eventData: {
          deal_id: dealId,
          team_members: data.results,
          recipient_list: recipientList,
        },
      });
    })
    .catch(error => console.log('Error tracking voucher submission event'));
};

const submitVoucher = voucherId => (dispatch, getState) => {
  trackVoucherSubmitted(getState);

  return vouchersService.submitVoucher(voucherId)
    .then((voucher) => {
      const currentVoucher = getState().voucher.voucher;
      clearLeaseAbstractionValidationForDeal(voucher.deal || currentVoucher.deal);
      dispatch(fetchVoucherSuccess({
        ...voucher,
        send_to_email_addresses: voucher.send_to_email_addresses || currentVoucher.send_to_email_addresses,
        recipients: voucher.recipients || currentVoucher.recipients,
      }));
      return voucher;
    })
    .catch(async (error) => {
      console.log('Error submitting voucher');
      let submitError = 'Unable to submit voucher. Please review the voucher and try again.';
      if (error?.json) {
        try {
          const errorData = await error.json();
          submitError = errorData?.message || errorData?.detail || submitError;
        } catch (parseError) {
          // Fall back to default message when error body cannot be parsed.
        }
      }
      return { submitError };
    });
};

const populateRecipients = voucherId => (dispatch) => {
  vouchersService.populateRecipients(voucherId)
    .then((recipients) => {
      dispatch(fetchRecipientsSuccess(recipients));
    })
    .catch(error => console.log('Error populating recipients of voucher'));
};

const addRecipient = (voucherId, recipient) => (dispatch) => {
  vouchersService.addRecipient(voucherId, recipient)
    .then((recipients) => {
      dispatch(fetchRecipientsSuccess(recipients));
    })
    .catch(error => console.log('Error adding recipient to voucher'));
};

const addRecipients = (voucherId, recipients) => dispatch =>
  recipients.forEach(recipient =>
    dispatch(addRecipient(voucherId, recipient)));

const removeRecipient = (voucherId, recipient) => (dispatch) => {
  vouchersService.removeRecipient(voucherId, recipient)
    .then(recipients => dispatch(fetchRecipientsSuccess(recipients)))
    .catch(error => console.log('Error removing recipient from voucher'));
};

export {
  fetchVoucher,
  fetchVoucherByDealId,
  createVoucher,
  fetchOrCreateVoucher,
  updateVoucher,
  populateRecipients,
  addRecipient,
  addRecipients,
  removeRecipient,
  fetchOfficeTypes,
  fetchCommercialProperty,
  fetchMtaNaicsChoices,
  fetchMtaNaicsChoicesByCode,
  updateCompany,
  setUpdatedCompanyInfo,
  fetchCompanyById,
  createCommercialProperty,
  ensureCommercialProperty,
  deleteCommercialProperty,
  updateCommercialProperty,
  submitVoucher,
};
